import { useState, useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Autocomplete, TextField } from '@mui/material';
import { Flex } from '../../../../commonStyles';
import Dropdown from '../../../common/formControls/Dropdown';
import { selectMerges } from '../../../../features/merge/mergeSlice';
import { IMerge } from '../../../../types/merge';
import { CreateDealContext } from '../../../../context/CreateDealContext';
import { selectProject } from '../../../../features/project/projectSlice';

const CreateForm = () => {
  const { selectedAps, setSelectedAps, setAutoDocuments, autoDocuments } = useContext(CreateDealContext);
  const merges = useSelector(selectMerges);
  const project = useSelector(selectProject);

  const [apsNames, setAPSNames] = useState<string[]>([]);
  const [mergeNames, setMergeNames] = useState<any[]>([]);
  const hasUserSelectedAps = useRef(false);

  useEffect(() => {
    arrayOfMerges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merges]);

  useEffect(() => {
    if (selectedAps) {
      let documentsAttachToAps = project.mergeTemplates
        .filter((mergeTemplates: any) => {
          let documentTemplates = mergeTemplates.apsTemplates.find(
            (apsTemplate: any) =>
              apsTemplate.apsTemplate._id === selectedAps._id && (apsTemplate.attachToAps === 'back' || apsTemplate.attachToAps === 'front')
          );
          return documentTemplates;
        })
        .map((documents: any) => {
          return {
            name: documents.name,
            mergeTemplate: documents.mergeTemplate._id,
            file: null,
            totalPages: documents.mergeTemplate.totalPages,
            position: documents.apsTemplates.find((apsTemplate: any) => apsTemplate.apsTemplate._id === selectedAps._id).attachToAps,
          };
        });
      setAutoDocuments(documentsAttachToAps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAps]);

  const arrayOfMerges = () => {
    let aps = merges
      .filter((mergeFields: IMerge) => mergeFields.type === 'APS')
      .reverse()
      .sort((b: any, a: any) => a.default - b.default);
    let apsNames = aps.map((merge: IMerge) => merge.name);
    // Amendments/schedules that can be attached to the APS - not the APS itself, and not archived templates
    let other = merges.filter((mergeFields: IMerge) => mergeFields.type !== 'APS' && mergeFields.type !== 'Archive');

    if (!hasUserSelectedAps.current) {
      setSelectedAps(aps.find((mergeTemplate: IMerge) => mergeTemplate.default) ?? aps[0]);
    }

    setAPSNames(apsNames);
    setMergeNames(other.sort((a: any, b: any) => a.name.localeCompare(b.name)));
  };

  const handleAPSDropdownInput = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let mergeArray = merges.find((mergeTemplate: IMerge) => mergeTemplate.name === event.target.value);
    hasUserSelectedAps.current = true;
    setSelectedAps(mergeArray);
  };
  
  // autoDocuments holds both lists in one array (each item carries its position), front first.
  const frontDocuments = autoDocuments.filter((document: any) => document.position === 'front');
  const backDocuments = autoDocuments.filter((document: any) => document.position !== 'front');

  const handleAttachInput = (position: 'front' | 'back', newValue: any) => {
    const uniqueByMap = [
      ...new Map(
        newValue.map((item: any) => [
          item.name,
          {
            ...item,
            name: item.name,
            mergeTemplate: position === 'front' ? item.mergeTemplate ?? item._id : item._id,
            file: null,
            totalPages: item.totalPages,
            position,
          },
        ])
      ).values(),
    ];
    setAutoDocuments(position === 'front' ? [...uniqueByMap, ...backDocuments] : [...frontDocuments, ...uniqueByMap]);
  };

  return (
    <div>
      <h2>Attach Amendments/Schedules</h2>
      <Flex sx={{ mt: 2 }}>
        <Box
          sx={{
            flexGrow: 1,
            mr: 1,
          }}
        >
          <Dropdown
            title={'Attached APS'}
            menuList={apsNames}
            name={'options'}
            handleSelect={(e: any) => handleAPSDropdownInput(e)}
            value={selectedAps ? selectedAps.name : ''}
          />
        </Box>
      </Flex>
      <Flex sx={{ mt: 2 }}>
        <Box
          sx={{
            flexGrow: 1,
            mr: 1,
          }}
        >
          <Autocomplete
            multiple
            options={mergeNames.filter((mergeName: any) => !backDocuments.some((document: any) => document.name === mergeName.name))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
            disableClearable={false}
            freeSolo={false}
            value={frontDocuments}
            onChange={(event: any, newValue: any | null) => handleAttachInput('front', newValue)}
            renderInput={(params) => <TextField {...params} label="Attach Amendments or Schedules to the Front of the APS" size="medium" />}
          />
        </Box>
      </Flex>
      <Flex sx={{ mt: 2 }}>
        <Box
          sx={{
            flexGrow: 1,
            mr: 1,
          }}
        >
          <Autocomplete
            multiple
            options={mergeNames.filter((mergeName: any) => !frontDocuments.some((document: any) => document.name === mergeName.name))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
            disableClearable={false}
            freeSolo={false}
            value={backDocuments}
            onChange={(event: any, newValue: any | null) => handleAttachInput('back', newValue)}
            renderInput={(params) => <TextField {...params} label="Attach Amendments or Schedules to the Back of the APS" size="medium" />}
          />
        </Box>
      </Flex>
    </div>
  );
};

export default CreateForm;
